import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";
import { generatePageTitle, generateCanonicalUrl } from "@/lib/seoConfig";

const TermsOfUse = () => {
  return (
    <>
      <Helmet>
        <title>{generatePageTitle("Terms of Use")}</title>
        <meta name="description" content="iQ Texting Terms of Use. Review our terms and conditions for using our AI-powered SMS messaging platform." />
        <link rel="canonical" href={generateCanonicalUrl("/terms-of-use")} />
        <meta property="og:title" content={generatePageTitle("Terms of Use")} />
        <meta property="og:description" content="Review iQ Texting's terms and conditions for using our platform." />
        <meta property="og:url" content={generateCanonicalUrl("/terms-of-use")} />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-6">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            Terms of Use
          </h1>
          <p className="text-muted-foreground mt-2">Last updated: March 2025</p>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground">
              By accessing and using iQ Texting services, you accept and agree to be bound by these Terms of Use. 
              If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
            <p className="text-muted-foreground">
              iQ Texting provides AI-powered SMS communication platform services for businesses. We reserve the right 
              to modify, suspend, or discontinue any part of the service at any time.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. User Responsibilities</h2>
            <p className="text-muted-foreground mb-2">You agree to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Not use the service for spam or unsolicited messages</li>
              <li>Not engage in fraudulent or illegal activities</li>
              <li>Respect the intellectual property rights of others</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Prohibited Uses</h2>
            <p className="text-muted-foreground mb-2">You may not:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Use the service to send spam or unsolicited commercial messages</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the service</li>
              <li>Impersonate any person or entity</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Intellectual Property</h2>
            <p className="text-muted-foreground">
              All content, features, and functionality of iQ Texting are owned by us and are protected by 
              copyright, trademark, and other intellectual property laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Limitation of Liability</h2>
            <p className="text-muted-foreground">
              To the maximum extent permitted by law, iQ Texting shall not be liable for any indirect, incidental, 
              special, consequential, or punitive damages resulting from your use of the service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Termination</h2>
            <p className="text-muted-foreground">
              We reserve the right to terminate or suspend your access to the service at any time, with or without 
              notice, for conduct that we believe violates these Terms of Use or is harmful to other users.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Changes to Terms</h2>
            <p className="text-muted-foreground">
              We may modify these Terms of Use at any time. Continued use of the service after changes constitutes 
              acceptance of the modified terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Contact Information</h2>
            <p className="text-muted-foreground">
              For questions about these Terms of Use, please contact us at legal@iqtexting.com
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
    </>
  );
};

export default TermsOfUse;
